import React, { useMemo } from 'react';
import { CheckCircle2, AlertCircle, Lightbulb, Target } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const CATEGORY_LABELS = {
  headline: 'Headline',
  about: 'About',
  experience: 'Experience',
  skills: 'Skills',
};

const SIGNAL_LABELS = {
  // Headline
  uses_metric: 'Has a number',
  is_concise: 'Concise (60-220 chars)',
  has_role_clarity: 'Role is clear',
  names_outcome: 'Names the outcome',
  no_buzzword_overload: 'No buzzword overload',
  // About
  word_count_in_range: '220-380 words',
  first_person_voice: 'First-person voice',
  has_metrics: '2+ concrete numbers',
  has_structure: '3+ paragraphs',
  has_action_verbs: '3+ action verbs',
  has_closing_cta: 'Closing CTA',
  // Experience
  has_recent_role: 'Recent role present',
  has_metrics_per_role: 'Metric per role',
  has_outcome_bullets: 'Bulleted outcomes',
  has_3_plus_roles: '3+ roles',
  // Skills
  has_10_or_more: '10+ skills',
  has_strategic_terms: 'Strategic terms',
  not_truncated_by_pdf: 'Not PDF-truncated',
};

/**
 * "Why didn't I hit potential?" — per-category gap explainer card.
 *
 * For each category where the user's current score is more than 4 points
 * below their potential, we render:
 *   • A current-vs-potential progress bar
 *   • Chips for structural signals already PRESENT (green) and MISSING (red)
 *   • An actionable tip line for each missing signal
 *
 * Hidden when there is no gap_analysis or the gaps are negligible.
 */
export default function WhyNotPotential({ scoring }) {
  const gap = scoring?.gap_analysis || null;

  const meaningful = useMemo(() => {
    if (!gap) return [];
    return Object.entries(gap)
      .filter(([, v]) => {
        if (!v) return false;
        const cur = v.current_score ?? 0;
        const pot = v.potential_score ?? 0;
        // Show only categories where there is still a meaningful gap and at
        // least one missing structural signal.
        return pot - cur > 4 && (v.missing_signals || []).length > 0;
      })
      .map(([k, v]) => ({ category: k, ...v }));
  }, [gap]);

  if (!meaningful.length) return null;

  const totalMissing = meaningful.reduce(
    (acc, c) => acc + (c.missing_signals?.length || 0),
    0,
  );

  return (
    <Card className="border-border" data-testid="why-not-potential-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <Target size={16} className="text-primary" />
          <h3 className="font-semibold">Why you haven&apos;t hit potential yet</h3>
          <Badge
            variant="secondary"
            className="font-mono-edge text-[10px] ml-1"
            data-testid="why-not-potential-count"
          >
            {totalMissing} signal{totalMissing === 1 ? '' : 's'} missing
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          These are the specific structural signals our scorer still can&apos;t
          find in your profile. Each one is mechanical &mdash; add them and the
          score moves immediately on your next audit.
        </p>

        <div className="space-y-5">
          {meaningful.map((cat) => {
            const cur = cat.current_score ?? 0;
            const pot = cat.potential_score ?? 0;
            const pct = pot > 0 ? (cur / pot) * 100 : 0;
            const label = CATEGORY_LABELS[cat.category] || cat.category;
            return (
              <div
                key={cat.category}
                className="rounded-lg border border-border bg-secondary/40 p-4"
                data-testid={`why-not-potential-row-${cat.category}`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
                  <h4 className="font-semibold text-sm">{label}</h4>
                  <span className="font-mono-edge text-[11px] text-muted-foreground">
                    {cur} <span className="text-foreground/50">/</span> {pot}{' '}
                    <span className="text-foreground/50">potential</span>
                  </span>
                </div>
                <Progress value={pct} className="h-1.5 mb-3" />

                {/* Present + missing signal chips */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(cat.present_signals || []).map((s) => (
                    <Badge
                      key={`p-${s}`}
                      className="font-mono-edge text-[10px] bg-[hsl(var(--success))]/12 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 inline-flex items-center gap-1"
                      data-testid={`why-not-potential-present-${cat.category}-${s}`}
                    >
                      <CheckCircle2 size={9} />
                      {SIGNAL_LABELS[s] || s}
                    </Badge>
                  ))}
                  {(cat.missing_signals || []).map((s) => (
                    <Badge
                      key={`m-${s}`}
                      className="font-mono-edge text-[10px] bg-destructive/12 text-destructive border-destructive/30 inline-flex items-center gap-1"
                      data-testid={`why-not-potential-missing-${cat.category}-${s}`}
                    >
                      <AlertCircle size={9} />
                      {SIGNAL_LABELS[s] || s}
                    </Badge>
                  ))}
                </div>

                {/* Tips */}
                {(cat.tips || []).length > 0 && (
                  <ul className="space-y-1.5">
                    {(cat.tips || []).map((tip, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-xs text-foreground/85"
                        data-testid={`why-not-potential-tip-${cat.category}-${i}`}
                      >
                        <Lightbulb
                          size={12}
                          className="text-[hsl(var(--accent))] shrink-0 mt-0.5"
                        />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
